// src/components/Popover.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom'; // << IMPORT ReactDOM

function Popover({ content, description, children, triggerClassName = "" }) {
    const [visible, setVisible] = useState(false);
    const triggerRef = useRef(null); // Ref for the trigger element
    const popoverRef = useRef(null); // Ref for the popover content itself (now in portal)

    const [popoverStyle, setPopoverStyle] = useState({
        display: 'none', // Initially hidden
        top: 0,
        left: 0,
    });
    const [arrowStyle, setArrowStyle] = useState({});
    const [shouldFlip, setShouldFlip] = useState(false);

    // Get the portal root element (make sure it exists in your index.html)
    const popoverRoot = document.getElementById('popover-root');

    const showPopover = () => setVisible(true);
    const hidePopover = () => setVisible(false);

    const calculatePosition = useCallback(() => {
        if (!triggerRef.current || !popoverRef.current || !visible) {
            // If not visible or refs not set, ensure it's hidden
            setPopoverStyle(prev => ({ ...prev, display: 'none' }));
            return;
        }

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const popoverRect = popoverRef.current.getBoundingClientRect(); // Get dimensions of actual popover

        const spaceBelow = window.innerHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;
        const popoverHeight = popoverRect.height;
        const popoverWidth = popoverRect.width;
        const arrowHeight = 8; // Height of your arrow
        const margin = 8; // Margin between trigger and popover

        let top, left;
        let flip = false;

        // Decide to flip or not
        if (spaceBelow < (popoverHeight + margin + arrowHeight) && spaceAbove > (popoverHeight + margin + arrowHeight)) {
            flip = true; // Flip (show above)
            top = triggerRect.top + window.scrollY - popoverHeight - margin - arrowHeight;
        } else {
            flip = false; // Default (show below)
            top = triggerRect.bottom + window.scrollY + margin + arrowHeight;
        }

        // Basic left alignment (can be enhanced for centering or avoiding screen edges)
        left = triggerRect.left + window.scrollX + (triggerRect.width / 2) - (popoverWidth / 2);

        // Keep popover within viewport horizontally
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth; // Approx scrollbar width
        if (left < margin) {
            left = margin;
        } else if (left + popoverWidth > window.innerWidth - margin - scrollbarWidth) {
            left = window.innerWidth - popoverWidth - margin - scrollbarWidth;
        }

        setShouldFlip(flip);
        setPopoverStyle({
            display: 'block',
            position: 'absolute', // Positioned relative to the document now
            top: `${top}px`,
            left: `${left}px`,
            zIndex: 1050, // High z-index for portal content
            background: '#9c1521', // Your background color
            // Other base styles from your original popover div
            color: 'white',
            width: '20rem', // w-80
            padding: '0.75rem', // p-3
            border: '1px solid white',
            borderRadius: '0.375rem', // rounded-md
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)', // shadow-lg
        });

        // Arrow position relative to the popover body
        // The arrow's `left` needs to be calculated relative to the popover's `left`
        // and the trigger's horizontal center.
        const arrowLeftOffset = triggerRect.left + window.scrollX + (triggerRect.width / 2) - left - arrowHeight; // arrowHeight is half its width

        setArrowStyle({
            position: 'absolute',
            left: `${arrowLeftOffset}px`,
            width: '0',
            height: '0',
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            ...(flip
                ? { top: '100%', borderBottom: '8px solid #9c1521' } // Arrow at bottom, pointing down
                : { bottom: '100%', borderTop: '8px solid #9c1521' }) // Arrow at top, pointing up
        });

    }, [visible]); // Rerun when visibility changes

    useEffect(() => {
        if (visible) {
            // Calculate position after the popover content (in portal) has had a chance to render and get dimensions
            requestAnimationFrame(() => {
                calculatePosition();
            });

            window.addEventListener('resize', calculatePosition);
            window.addEventListener('scroll', calculatePosition, true); // Capture scroll events

            return () => {
                window.removeEventListener('resize', calculatePosition);
                window.removeEventListener('scroll', calculatePosition, true);
            };
        } else {
            setPopoverStyle(prev => ({ ...prev, display: 'none' })); // Hide when not visible
        }
    }, [visible, calculatePosition]);

    const defaultTriggerStyle = "cursor-pointer text-pcc-cardinal font-bold";

    const popoverItself = visible ? (
        <div ref={popoverRef} style={popoverStyle}>
            {description && (
                <div className="font-bold border-b border-white pb-1 mb-3 text-sm">
                    {description}
                </div>
            )}
            <div className="text-xs">
                {content}
            </div>
            <div style={arrowStyle} /> {/* Arrow */}
        </div>
    ) : null;

    return (
        // The trigger remains in its original place in the DOM
        <>
            <span
                ref={triggerRef}
                onMouseEnter={showPopover}
                onMouseLeave={hidePopover}
                className={triggerClassName || defaultTriggerStyle}
            >
                {children}
            </span>
            {/* The popover content is rendered into the portal root */}
            {popoverRoot && ReactDOM.createPortal(popoverItself, popoverRoot)}
        </>
    );
}

export default Popover;